import { Component, For, Show, createMemo } from "solid-js";
import { Button } from "../ui/Button";
import { diffLines } from "../../utils/diffLines";

export interface ProposalDiffProps {
  before: string | null;
  after: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  class?: string;
}

const rowClass = {
  context: "text-ink-2",
  added: "bg-signal-green-tint text-signal-green",
  removed: "bg-signal-red-tint text-signal-red line-through",
} as const;
const marker = { context: " ", added: "+", removed: "−" } as const;
const srPrefix = {
  context: "",
  added: "Added line:",
  removed: "Removed line:",
} as const;

/**
 * `+`/`−` line diff of an AI proposal; the markers and strikethrough carry
 * the meaning, the tints reinforce it (FLOW-780 D3).
 */
export const ProposalDiff: Component<ProposalDiffProps> = (props) => {
  const lines = createMemo(() => diffLines(props.before ?? "", props.after));
  return (
    <div class={"rounded border border-line bg-surface " + (props.class ?? "")}>
      <ul role="list" class="font-mono text-xs leading-5 py-1">
        <For each={lines()}>
          {(line) => (
            <li
              class={
                "flex gap-2 px-2 whitespace-pre-wrap break-words " +
                rowClass[line.kind]
              }
            >
              <span class="sr-only">{srPrefix[line.kind]}</span>
              <span aria-hidden="true" class="w-3 shrink-0 select-none">
                {marker[line.kind]}
              </span>
              <span class="min-w-0">{line.text}</span>
            </li>
          )}
        </For>
      </ul>
      <Show when={props.onAccept || props.onDismiss}>
        <div class="flex items-center gap-2 px-2 py-1.5 border-t border-line">
          <Show when={props.onAccept}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => props.onAccept?.()}
            >
              Accept
            </Button>
          </Show>
          <Show when={props.onDismiss}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => props.onDismiss?.()}
            >
              Dismiss
            </Button>
          </Show>
        </div>
      </Show>
    </div>
  );
};
